/**
 * SidecarPanel — sidebar panel showing all registered sidecars and their runtime status.
 *
 * Renders every sidecar from the registry, merges live status from the platform sidecar manager,
 * and shows which plugins are bound to each sidecar.
 * Clicking a sidecar opens its detail view in the main content area.
 */

import {
    resolveSidecarManifest,
    type SidecarManifest,
} from "@magic-md/engine-client/protocol";
import { useI18n, useT } from "@magic-md/i18n/react";
import {
    PowerOff,
    RefreshCw,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { useEditorStore } from "../store/editor-store";
import { useEngineOverviewStore } from "../store/engine-overview-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import {
    isCloudSidecarListItem,
    SidecarList,
    type SidecarListItemModel,
} from "./SidecarList";
import { SidebarCloseButton } from "./SidebarCloseButton";

/* ── Helpers ── */

/** Build a map of sidecar id → plugin names from the plugin registry */
function buildPluginMap(): Map<string, string[]> {
    const map = new Map<string, string[]>();
    for (const [name, plugin] of pluginUIRegistry.getAll()) {
        for (const b of plugin.manifest.backends ?? []) {
            const list = map.get(b.id) ?? [];
            list.push(name);
            map.set(b.id, list);
        }
    }
    return map;
}

/** Merge a manifest with its live status */
interface SidecarEntry {
    manifest: SidecarManifest;
    alive: boolean;
    plugins: string[];
    sourceKind?: "local" | "cloud";
    transportType?: string;
}

/* ── Component ── */

export function SidecarPanel() {
    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between h-8 px-3 border-b border-border shrink-0">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Sidecars
                </span>
                <SidebarCloseButton />
            </div>
            <SidecarContent />
        </div>
    );
}

export function SidecarContent() {
    const i18n = useI18n();
    const t = useT("editor");
    const selectedSidecarId = useEditorStore((s) => s.selectedSidecarId);
    const setSelectedSidecarId = useEditorStore((s) => s.setSelectedSidecarId);
    const sidecarInfos = useEngineOverviewStore((s) => s.sidecars);
    const loading = useEngineOverviewStore((s) => s.loadingScopes.sidecars);
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);
    const refreshNow = useEngineOverviewStore((s) => s.refreshNow);

    useEffect(() => {
        return retainOverviewScopes(["sidecars"]);
    }, [retainOverviewScopes]);

    const pluginMap = useMemo(() => buildPluginMap(), []);

    /** Build entries from engine response (already includes all known sidecars) */
    const entries: SidecarEntry[] = useMemo(() => {
        const items: SidecarEntry[] = sidecarInfos.map((s) => ({
            manifest: s.manifest
                ? resolveSidecarManifest(
                    s.manifest as unknown as Record<string, unknown>,
                    undefined,
                    { locale: i18n.locale, fallbackLocale: i18n.fallbackLocale },
                ) as unknown as SidecarManifest
                : {
                    id: s.id,
                    name: s.id,
                    version: "",
                    description: s.command ?? s.id,
                    command: s.command,
                    runtime: "other",
                    protocol: "jsonrpc-stdio",
                    capabilities: s.capabilities.map((c) => ({
                        method: c,
                        description: "",
                    })),
                },
            alive: s.alive,
            plugins: pluginMap.get(s.id) ?? [],
            sourceKind: s.sourceKind,
            transportType: s.transport?.type,
        }));

        // Sort: running first, then alphabetical
        items.sort((a, b) => {
            const aRun = a.alive ? 0 : 1;
            const bRun = b.alive ? 0 : 1;
            if (aRun !== bRun) return aRun - bRun;
            return a.manifest.id.localeCompare(b.manifest.id);
        });

        return items;
    }, [i18n.fallbackLocale, i18n.locale, sidecarInfos, pluginMap]);

    const items = useMemo<SidecarListItemModel[]>(
        () =>
            entries.map((entry) => ({
                id: entry.manifest.id,
                title: entry.manifest.title ?? entry.manifest.id,
                icon: entry.manifest.icon,
                description: entry.manifest.description,
                alive: entry.alive,
                sourceKind: entry.sourceKind,
                transportType: entry.transportType,
                manifestSourceKind: entry.manifest.sourceKind,
                manifestTransportType: entry.manifest.transport?.type,
                searchText: [
                    entry.manifest.name,
                    entry.manifest.overview,
                    ...entry.plugins,
                    ...entry.manifest.capabilities.map((capability) => capability.method),
                ].filter((value): value is string => !!value),
            })),
        [entries],
    );

    const handleShutdown = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        try {
            await getPlatformService().engine.sidecarShutdown(id);
            await refreshNow(["sidecars"]);
        } catch {
            // ignore
        }
    };

    return (
        <>
            <SidecarList
                items={items}
                selectedId={selectedSidecarId}
                onSelect={setSelectedSidecarId}
                searchPlaceholder={t("sidecarPanel.searchPlaceholder")}
                emptyMessage={t("sidecarPanel.empty")}
                noMatchesMessage={t("sidecarPanel.noMatching")}
                toolbarAction={
                    <button
                        type="button"
                        className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors shrink-0"
                        onClick={() => {
                            void refreshNow(["sidecars"]);
                        }}
                        title={t("common.refresh")}
                    >
                        <RefreshCw size={11} className={cn(loading ? "animate-spin" : "")} />
                    </button>
                }
                rightAction={(item) =>
                    item.alive && !isCloudSidecarListItem(item) ? (
                        <button
                            type="button"
                            className={cn(
                                "flex h-5 w-5 items-center justify-center rounded opacity-0 transition-all group-hover:opacity-100",
                                "theme-accent-icon-button",
                            )}
                            style={getThemeAccentStyle("error")}
                            onClick={(event) => handleShutdown(event, item.id)}
                            title={t("sidecarPanel.shutdown")}
                        >
                            <PowerOff size={11} />
                        </button>
                    ) : null
                }
            />
        </>
    );
}
